
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useTeacherMatch } from '@/hooks/useTeacherMatch';
import { TeacherMatchFilters } from '@/components/admin/teacher-match/TeacherMatchFilters';
import { TeacherMatchCard } from '@/components/admin/teacher-match/TeacherMatchCard';
import { Search, CheckSquare, Square, Send, Loader2 } from 'lucide-react';
import { Link2 } from 'lucide-react';
import { TeacherLineLinkManagerDialog } from '@/components/admin/teacher-match/TeacherLineLinkManagerDialog';
import { MatchHistoryList } from '@/components/admin/teacher-match/MatchHistoryList';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useStudentByCode } from '@/hooks/useStudentMatchSearch';
import { useQuery } from '@tanstack/react-query';
import { Checkbox } from '@/components/ui/checkbox';

const CLASS_TYPE_OPTIONS = ['Premiere', 'Private class'];
// Grade options for the popup form (ป.1 – ม.6) plus a "ไม่ระบุ" escape hatch
// for cases where admin doesn't know the student's grade.
const GRADE_OPTIONS = [
  'ป.1', 'ป.2', 'ป.3', 'ป.4', 'ป.5', 'ป.6',
  'ม.1', 'ม.2', 'ม.3', 'ม.4', 'ม.5', 'ม.6',
  'ไม่ระบุ',
];
const COURSE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'Regular', label: 'หลักสูตรไทย' },
  { value: 'Inter', label: 'หลักสูตรอินเตอร์' },
];
const COMMUNICATION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'EN_EN', label: 'EN-EN (เนื้อหา EN / สื่อสาร EN)' },
  { value: 'EN_TH', label: 'EN-TH (เนื้อหา EN / สื่อสาร TH)' },
];
const PARENT_CONCERN_OPTIONS = [
  'สมาธิสั้น / ไม่มีสมาธิเรียน',
  'พื้นฐานไม่แน่น',
  'ตามเพื่อนไม่ทัน',
  'ไม่ชอบ/กลัววิชานี้',
  'ขี้อาย / ไม่กล้าถาม',
  'อ่อนภาษาอังกฤษ',
];

interface LineDetailsState {
  nickname: string;
  firstName: string;
  lastName: string;
  school: string;
  grade: string;           // ป.1-ม.6 | ไม่ระบุ (required)
  trialTopic: string;
  hours: string;
  classType: string;       // Premiere | Private class
  courseType: string;      // Regular | Inter
  interSubType: string;    // EN_EN | EN_TH (display label = "การสื่อสาร")
  desiredStartDate: string;
  parentExpectations: string;
  parentConcerns: string[];
  parentConcernsOther: string;
}

const EMPTY_LD: LineDetailsState = {
  nickname: '', firstName: '', lastName: '', school: '', grade: '', trialTopic: '',
  hours: '', classType: '', courseType: '', interSubType: '', desiredStartDate: '',
  parentExpectations: '', parentConcerns: [], parentConcernsOther: '',
};

const AdminTeacherMatchPage: React.FC = () => {
  const {
    teachers,
    totalCount,
    page,
    totalPages,
    setPage,
    isLoading,
    error,
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    filterOptions,
    getMatchedAttributes,
  } = useTeacherMatch();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lineManagerOpen, setLineManagerOpen] = useState(false);

  // Selection only applies to teachers currently loaded on this page.
  // Clear selection whenever the page or filters change to avoid silently
  // losing selections from teachers that are no longer in `teachers`.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, filters]);

  // Expire selection (max 48hr)
  const [expireHours, setExpireHours] = useState<string>('24');
  const [customExpire, setCustomExpire] = useState<string>('');

  // Student info derived from filters (read-only in popup)
  const { data: lockedStudent } = useStudentByCode(filters.selectedStudentCode);
  const { data: lockedTrial } = useQuery({
    enabled: !!filters.selectedClassTrialId,
    queryKey: ['match-popup-trial', filters.selectedClassTrialId],
    queryFn: async () => {
      const { data, error } = await supabase.from('class_trials')
        .select('id, student_firstname, student_lastname, student_nickname, student_name, school, grade, trial_topic, desired_hours, class_type, desired_start_date, parent_expectations_trial, parent_concerns')
        .eq('id', filters.selectedClassTrialId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Optional fetch of fishsix_students school when using system student
  const { data: lockedStudentExtra } = useQuery({
    enabled: !!filters.selectedStudentCode,
    queryKey: ['match-popup-student-extra', filters.selectedStudentCode],
    queryFn: async () => {
      const { data, error } = await (supabase as any).schema('student').from('fishsix_students')
        .select('code, school, education_level')
        .eq('code', filters.selectedStudentCode)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Line details state (the 11 form fields in the popup)
  const [lineDetails, setLineDetails] = useState<LineDetailsState>(EMPTY_LD);
  const updateLD = useCallback(<K extends keyof LineDetailsState>(key: K, value: LineDetailsState[K]) => {
    setLineDetails(prev => ({ ...prev, [key]: value }));
  }, []);

  // Auto-fill when dialog opens or when source data arrives
  useEffect(() => {
    if (!confirmOpen) return;
    setLineDetails(prev => {
      const trial = lockedTrial as any;
      const student = lockedStudent as any;
      const studentExtra = lockedStudentExtra as any;
      // Split trial.class_type (e.g. "Premiere/TH") into classType + courseType
      let splitClassType = '';
      let splitCourseType = '';
      const tct: string | undefined = trial?.class_type;
      if (tct) {
        const slashIdx = tct.lastIndexOf('/');
        if (slashIdx > 0) {
          const left = tct.slice(0, slashIdx);
          const right = tct.slice(slashIdx + 1);
          if (CLASS_TYPE_OPTIONS.includes(left)) splitClassType = left;
          if (right === 'TH') splitCourseType = 'Regular';
          else if (right === 'Inter') splitCourseType = 'Inter';
        }
      }
      // Grade: safe-match against GRADE_OPTIONS so stale/free-text values
      // (e.g. "ม. 3" with a space) don't break the Select. Falls back to ''
      // → user must pick explicitly (including "ไม่ระบุ").
      const safeGrade = (raw: string | null | undefined): string => {
        if (!raw) return '';
        const norm = raw.trim().toLowerCase();
        return GRADE_OPTIONS.find(g => g.toLowerCase() === norm) || '';
      };
      const next: LineDetailsState = {
        nickname: prev.nickname || trial?.student_nickname || student?.nickname || '',
        firstName: prev.firstName || trial?.student_firstname || student?.first_name || '',
        lastName: prev.lastName || trial?.student_lastname || student?.last_name || '',
        school: prev.school || trial?.school || studentExtra?.school || '',
        grade: prev.grade || safeGrade(trial?.grade) || safeGrade(studentExtra?.education_level) || '',
        trialTopic: prev.trialTopic || trial?.trial_topic || '',
        // Hours: user-entered only, no auto-fill from DB
        hours: prev.hours,
        classType: prev.classType || splitClassType,
        // courseType: prefer filter; fallback to trial DB split
        courseType: prev.courseType || filters.courseTypes[0] || splitCourseType,
        // interSubType: from filter (no DB source)
        interSubType: prev.interSubType || filters.interSubTypes[0] || '',
        desiredStartDate: prev.desiredStartDate || trial?.desired_start_date || '',
        parentExpectations: prev.parentExpectations || trial?.parent_expectations_trial || '',
        parentConcerns: prev.parentConcerns.length ? prev.parentConcerns : [],
        parentConcernsOther: prev.parentConcernsOther || trial?.parent_concerns || '',
      };
      return next;
    });
  }, [confirmOpen, lockedTrial, lockedStudent, lockedStudentExtra, filters.courseTypes, filters.interSubTypes]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!confirmOpen) setLineDetails(EMPTY_LD);
  }, [confirmOpen]);

  const picked = (() => {
    if (filters.studentMode === 'system' && filters.selectedStudentCode) {
      return {
        student_code: filters.selectedStudentCode,
        class_trial_id: null as string | null,
        student_display_name: null as string | null,
        first_name: lockedStudent?.first_name || filters.selectedStudentCode,
        nickname: lockedStudent?.nickname || '',
        label: `${lockedStudent?.first_name || ''}${lockedStudent?.nickname ? ` (${lockedStudent.nickname})` : ''} — ${filters.selectedStudentCode}`,
      };
    }
    if (filters.studentMode === 'trial' && filters.selectedClassTrialId) {
      const fn = lockedTrial?.student_firstname || lockedTrial?.student_name || '-';
      return {
        student_code: null,
        class_trial_id: filters.selectedClassTrialId,
        student_display_name: null,
        first_name: fn,
        nickname: lockedTrial?.student_nickname || '',
        label: `${fn}${lockedTrial?.student_nickname ? ` (${lockedTrial.student_nickname})` : ''} — ทดลองเรียน`,
      };
    }
    if (filters.studentMode === 'manual' && filters.selectedStudentDisplayName) {
      return {
        student_code: null,
        class_trial_id: null,
        student_display_name: filters.selectedStudentDisplayName,
        first_name: filters.selectedStudentDisplayName,
        nickname: '',
        label: `${filters.selectedStudentDisplayName} — พิมพ์เอง`,
      };
    }
    return {
      student_code: null as string | null,
      class_trial_id: null as string | null,
      student_display_name: null as string | null,
      first_name: '-',
      nickname: '',
      label: '',
    };
  })();

  const toggleSelect = useCallback((id: string) => {
    const t = teachers.find(x => x.id === id);
    if (t && !t.has_line_link) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [teachers]);

  const selectAll = useCallback(() => {
    // Select all teachers visible on the current page that have a LINE link.
    setSelectedIds(prev => {
      const next = new Set(prev);
      teachers.filter(t => t.has_line_link).forEach(t => next.add(t.id));
      return next;
    });
  }, [teachers]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // selectedIds may include teachers from other pages; only those on the current
  // page are available here for display in the cards. The confirm dialog uses
  // teachers loaded so far for preview but the actual send uses all selected codes.
  const selectedTeachers = teachers.filter(t => selectedIds.has(t.id));

  // Build smart page list: 1, ..., current-1, current, current+1, ..., last
  const pageNumbers = useMemo<(number | 'ellipsis')[]>(() => {
    const total = totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | 'ellipsis')[] = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(total - 1, page + 1);
    if (start > 2) pages.push('ellipsis');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push('ellipsis');
    pages.push(total);
    return pages;
  }, [page, totalPages]);

  const WEEKDAY_LABELS = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
  const openConfirm = () => {
    // Allow the button to always be clickable; validate here and show a toast
    // so the user knows exactly what is missing before the popup can open.
    const missing: string[] = [];
    if (selectedIds.size === 0) missing.push('เลือกครูที่ต้องการส่ง (อย่างน้อย 1 คน)');
    const hasTimeSlot = filters.availabilitySlots.some(s => s.start && s.end);
    if (!hasTimeSlot) missing.push('กรอกช่วงเวลาในตัวกรอง (อย่างน้อย 1 ช่วง)');
    if (filters.subjects.length === 0) missing.push('เลือกวิชาในตัวกรอง (อย่างน้อย 1 วิชา)');
    if (filters.educationStages.length === 0) missing.push('เลือกระดับชั้นในตัวกรอง (อย่างน้อย 1 ระดับ)');
    if (filters.branches.length === 0) missing.push('เลือกสาขาในตัวกรอง (อย่างน้อย 1 สาขา)');
    if (filters.courseTypes.length === 0) {
      missing.push('เลือก Regular / Inter ในตัวกรอง');
    } else if (filters.courseTypes.includes('Inter') && filters.interSubTypes.length === 0) {
      // Only required when Inter is in the selection — Regular alone doesn't need it
      missing.push('เลือกการสื่อสาร (EN_EN / EN_TH) เพราะติ๊ก Inter ไว้');
    }
    if (missing.length > 0) {
      toast({
        title: 'ยังไม่สามารถเปิดหน้ายืนยันได้',
        description: missing.join(' • '),
        variant: 'destructive',
      });
      return;
    }
    setConfirmOpen(true);
  };

  const slotsText = filters.availabilitySlots
    .map(s => `${WEEKDAY_LABELS[s.day] || ''}${s.start && s.end ? ` ${s.start}-${s.end}` : ''}`)
    .filter(Boolean)
    .join(', ');

  // Derive read-only day/time chips from availabilitySlots (unique)
  const slotDays = useMemo(() => {
    const set = new Set<string>();
    filters.availabilitySlots.forEach(s => { if (WEEKDAY_LABELS[s.day]) set.add(WEEKDAY_LABELS[s.day]); });
    return Array.from(set);
  }, [filters.availabilitySlots]);
  const slotTimes = useMemo(() => {
    const set = new Set<string>();
    filters.availabilitySlots.forEach(s => { if (s.start && s.end) set.add(`${s.start}-${s.end}`); });
    return Array.from(set);
  }, [filters.availabilitySlots]);
  // Day + time combined pairs for LINE message (e.g. "จันทร์ 10:00-12:00")
  const slotPairs = useMemo(() => {
    const seen = new Set<string>();
    const pairs: string[] = [];
    filters.availabilitySlots.forEach(s => {
      const dayLbl = WEEKDAY_LABELS[s.day];
      if (!dayLbl) return;
      const time = s.start && s.end ? ` ${s.start}-${s.end}` : '';
      const pair = `${dayLbl}${time}`;
      if (!seen.has(pair)) {
        seen.add(pair);
        pairs.push(pair);
      }
    });
    return pairs;
  }, [filters.availabilitySlots]);

  // Teacher-attribute snapshot from filters — shown read-only on popup and
  // appended to the LINE message under a second section "คุณสมบัติครู".
  // Empty filters render as "-" (per product decision).
  const fmtAttrList = (arr: Array<string | number>): string =>
    arr.length === 0 ? '-' : arr.join(', ');
  const fmtAttrLevels = (arr: number[]): string =>
    arr.length === 0 ? '-' : arr.map(v => `Lv.${v}`).join(', ');
  const teacherAttrsDisplay = useMemo(() => ({
    specialized: fmtAttrList(filters.specialized),
    teachingStyles: fmtAttrList(filters.teachingStyles),
    styleLevels: fmtAttrLevels(filters.styleLevels),
    personality: fmtAttrList(filters.personality),
    studentProficiency: fmtAttrList(filters.studentProficiency),
    gender: fmtAttrList(filters.gender),
    trialLesson: fmtAttrList(filters.trialLesson),
  }), [
    filters.specialized,
    filters.teachingStyles,
    filters.styleLevels,
    filters.personality,
    filters.studentProficiency,
    filters.gender,
    filters.trialLesson,
  ]);

  const ctx = {
    datetime: slotsText || '-',
    subject: filters.subjects.join(', ') || '-',
    education_stage: filters.educationStages.join(', ') || '-',
    branch: filters.branches.join(', ') || '-',
    class_program: filters.courseTypes.join(', ') || '-',
    student_first_name: picked.first_name || '-',
    student_nickname: picked.nickname || '-',
  };

  const isInterType = lineDetails.courseType === 'Inter';

  // Build a preview message that matches the edge function's new format
  const buildLinePreview = (teacherDisplay: string) => {
    const lines: string[] = [`สวัสดีค่ะ คุณครู${teacherDisplay}`, '', 'มีงานสอนที่อยากให้ครูช่วยพิจารณารับ:'];

    // ── Section 1: ข้อมูลนักเรียน ──
    lines.push('', '📋 ข้อมูลนักเรียน');
    const fullName = [lineDetails.firstName, lineDetails.lastName].filter(Boolean).join(' ');
    if (lineDetails.nickname) lines.push(`👦 ชื่อเล่น: ${lineDetails.nickname}`);
    if (fullName) lines.push(`🪪 ชื่อ-นามสกุล: ${fullName}`);
    if (lineDetails.school) lines.push(`🏫 โรงเรียน: ${lineDetails.school}`);
    if (lineDetails.grade) lines.push(`📖 ระดับชั้น: ${lineDetails.grade}`);
    if (filters.subjects.length) lines.push(`📚 วิชา: ${filters.subjects.join(', ')}`);
    if (lineDetails.trialTopic) lines.push(`📝 หัวข้อที่ต้องการเรียนครั้งแรก: ${lineDetails.trialTopic}`);
    // Combine classType + courseType for display (e.g. "Premiere/TH" or "Private class/Inter")
    const courseLabel = lineDetails.courseType === 'Regular' ? 'TH' : lineDetails.courseType === 'Inter' ? 'Inter' : '';
    const combinedType = lineDetails.classType
      ? `${lineDetails.classType}${courseLabel ? `/${courseLabel}` : ''}`
      : '';
    const commLabel = lineDetails.interSubType === 'EN_EN' ? 'สื่อสารภาษาอังกฤษ'
      : lineDetails.interSubType === 'EN_TH' ? 'สื่อสารภาษาไทย'
      : lineDetails.interSubType;
    const typeStr = combinedType + (isInterType && commLabel ? ` (${commLabel})` : '');
    if (lineDetails.hours || typeStr) lines.push(`⏱ ชั่วโมง/ประเภท: ${lineDetails.hours ? `${lineDetails.hours} ชม.` : '-'}${typeStr ? ` • ${typeStr}` : ''}`);
    if (slotPairs.length) lines.push(`📅 วัน-เวลาที่ต้องการเรียน: ${slotPairs.join(', ')}`);
    if (lineDetails.desiredStartDate) lines.push(`🚀 วันเริ่มเรียน: ${lineDetails.desiredStartDate}`);
    if (lineDetails.parentExpectations) lines.push(`🎯 ความคาดหวัง: ${lineDetails.parentExpectations}`);
    const concerns = [...lineDetails.parentConcerns];
    if (lineDetails.parentConcernsOther) concerns.push(lineDetails.parentConcernsOther);
    if (concerns.length) lines.push(`⚠️ ปัญหาที่ผู้ปกครองกังวล: ${concerns.join(', ')}`);

    // ── Section 2: คุณสมบัติครูที่ต้องการ (จากตัวกรอง) ──
    // Extra blank line for clearer visual separation between sections in LINE
    lines.push('', '', '🎓 คุณสมบัติครูที่ต้องการ');
    lines.push(`📚 เฉพาะทาง: ${teacherAttrsDisplay.specialized}`);
    lines.push(`🎨 สไตล์การสอน: ${teacherAttrsDisplay.teachingStyles}`);
    lines.push(`⭐ ระดับสไตล์การสอน: ${teacherAttrsDisplay.styleLevels}`);
    lines.push(`💭 บุคลิกภาพ (MBTI): ${teacherAttrsDisplay.personality}`);
    lines.push(`📊 ระดับนักเรียน: ${teacherAttrsDisplay.studentProficiency}`);
    lines.push(`👤 เพศ: ${teacherAttrsDisplay.gender}`);
    lines.push(`🎒 คลาสทดลองเรียน: ${teacherAttrsDisplay.trialLesson}`);

    return lines.join('\n');
  };

  const previewMessage = (() => {
    const t = selectedTeachers[0];
    const greet = t ? `${t.name}${t.nickname ? ` (${t.nickname})` : ''}` : '[ชื่อครู]';
    return buildLinePreview(greet);
  })();

  // Student picked validation — locked to filter selection
  const studentReady = !!(picked.student_code || picked.class_trial_id || picked.student_display_name);

  // Compute expire_at (ISO)
  const computeExpireAt = (): string | null => {
    if (expireHours === 'custom') {
      if (!customExpire) return null;
      const d = new Date(customExpire);
      const max = new Date(Date.now() + 48 * 3600 * 1000);
      if (isNaN(d.getTime()) || d.getTime() <= Date.now() || d.getTime() > max.getTime()) return null;
      return d.toISOString();
    }
    const h = parseInt(expireHours, 10);
    if (!h || h < 1 || h > 48) return null;
    return new Date(Date.now() + h * 3600 * 1000).toISOString();
  };
  const expireAtIso = computeExpireAt();
  // LineDetails validation
  const ldErrors = useMemo(() => {
    const errs: string[] = [];
    if (!lineDetails.nickname.trim()) errs.push('ชื่อเล่น');
    if (!lineDetails.firstName.trim()) errs.push('ชื่อจริง');
    if (!lineDetails.grade) errs.push('ระดับชั้น');
    if (!lineDetails.classType) errs.push('ประเภทที่ลง');
    if (!lineDetails.courseType) errs.push('หลักสูตร');
    if (isInterType && !lineDetails.interSubType) errs.push('การสื่อสาร');
    if (slotDays.length === 0) errs.push('วันที่ต้องการเรียน (ตั้งใน filter)');
    return errs;
  }, [lineDetails, isInterType, slotDays]);
  const handleSend = async () => {
    // Same UX as the "ส่งครูที่เลือก" button: always clickable; surface what's
    // missing via a toast so the admin doesn't have to hunt for the inline
    // error text.
    const missing: string[] = [];
    if (!studentReady) missing.push('เลือกนักเรียน (ในตัวกรอง)');
    if (!expireAtIso) missing.push('ตั้งเวลาหมดอายุที่ถูกต้อง (ไม่เกิน 48 ชม. นับจากตอนนี้)');
    if (selectedTeachers.length === 0) missing.push('เลือกครูที่ต้องการส่ง (อย่างน้อย 1 คน)');
    if (ldErrors.length > 0) missing.push(`กรอก/เลือก: ${ldErrors.join(', ')}`);
    if (missing.length > 0) {
      toast({
        title: 'ยังส่งไม่ได้',
        description: missing.join(' • '),
        variant: 'destructive',
      });
      return;
    }
    setIsSending(true);
    try {
      const teacher_codes = selectedTeachers.map(t => t.code).filter(Boolean);
      const { data, error } = await supabase.functions.invoke('send-teacher-match-line', {
        body: {
          teacher_codes,
          context: ctx,
          student_code: picked.student_code || null,
          class_trial_id: picked.class_trial_id || null,
          student_display_name: picked.student_display_name || null,
          search_filters: filters,
          expire_at: expireAtIso,
          line_details: {
            nickname: lineDetails.nickname.trim(),
            firstName: lineDetails.firstName.trim(),
            lastName: lineDetails.lastName.trim(),
            school: lineDetails.school.trim(),
            grade: lineDetails.grade,
            // Subject mirrors the filter selection (admin can't edit on popup).
            // Joined with ", " if multiple — usually a single subject after auto-fill.
            subject: filters.subjects.join(', '),
            trialTopic: lineDetails.trialTopic.trim(),
            hours: lineDetails.hours ? Number(lineDetails.hours) : null,
            // Combine classType + courseType for LINE message (e.g. "Premiere/TH")
            classType: lineDetails.classType + (
              lineDetails.courseType === 'Regular' ? '/TH'
              : lineDetails.courseType === 'Inter' ? '/Inter'
              : ''
            ),
            // Convert internal code (EN_EN) -> display label for LINE message
            interSubType: isInterType
              ? (lineDetails.interSubType === 'EN_EN' ? 'สื่อสารภาษาอังกฤษ'
                : lineDetails.interSubType === 'EN_TH' ? 'สื่อสารภาษาไทย'
                : lineDetails.interSubType)
              : '',
            days: slotDays,
            times: slotTimes,
            slots: slotPairs,
            desiredStartDate: lineDetails.desiredStartDate,
            parentExpectations: lineDetails.parentExpectations.trim(),
            parentConcerns: lineDetails.parentConcerns,
            parentConcernsOther: lineDetails.parentConcernsOther.trim(),
            // Teacher-attribute snapshot from filters (read-only on popup,
            // rendered as section 2 "คุณสมบัติครูที่ต้องการ" in the LINE message)
            teacherAttrs: {
              specialized: filters.specialized,
              teachingStyles: filters.teachingStyles,
              styleLevels: filters.styleLevels,
              personality: filters.personality,
              studentProficiency: filters.studentProficiency,
              gender: filters.gender,
              trialLesson: filters.trialLesson,
            },
          },
        },
      });
      if (error) throw error;
      const sent = data?.sent ?? 0;
      const failed = data?.failed ?? 0;
      toast({
        title: failed > 0 ? 'ส่งเสร็จ (มีบางรายล้มเหลว)' : 'ส่งสำเร็จ',
        description: `สำเร็จ ${sent} คน, ล้มเหลว ${failed} คน`,
        variant: failed > 0 ? 'destructive' : 'default',
      });
      if (failed === 0) {
        setSelectedIds(new Set());
        setConfirmOpen(false);
      }
    } catch (err: any) {
      toast({ title: 'เกิดข้อผิดพลาด', description: err.message, variant: 'destructive' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AdminSidebar />
        <main className="flex-1 p-6 space-y-6 overflow-auto">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-foreground">แมทช์ครู</h1>
              <p className="text-sm text-muted-foreground">ค้นหาและกรองครูตามคุณสมบัติต่างๆ</p>
            </div>
            <Button variant="outline" onClick={() => setLineManagerOpen(true)}>
              <Link2 className="h-4 w-4 mr-2" />
              จัดการการเชื่อม LINE ของครู
            </Button>
          </div>

          <TeacherLineLinkManagerDialog open={lineManagerOpen} onOpenChange={setLineManagerOpen} />

          <Tabs defaultValue="search" className="w-full">
            <TabsList>
              <TabsTrigger value="search">ค้นหา & ส่งงาน</TabsTrigger>
              <TabsTrigger value="history">ประวัติคำขอ</TabsTrigger>
            </TabsList>
            <TabsContent value="search" className="space-y-6 mt-4">
          <TeacherMatchFilters
            filters={filters}
            filterOptions={filterOptions}
            onUpdateFilters={updateFilters}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            resultCount={teachers.length}
            totalCount={totalCount}
          />

          {/* Loading */}
          {isLoading && (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-40 w-full rounded-lg" />
              ))}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-center py-12 text-destructive">
              <p>เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
              <p className="text-sm">{(error as Error).message}</p>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !error && teachers.length === 0 && (
            <div className="text-center py-16 space-y-3">
              <Search className="h-12 w-12 mx-auto text-muted-foreground/40" />
              <p className="text-lg font-medium text-muted-foreground">ไม่พบครูที่ตรงกับเงื่อนไข</p>
              <p className="text-sm text-muted-foreground">ลองปรับตัวกรองใหม่</p>
            </div>
          )}

          {/* Selection Toolbar + Results */}
          {!isLoading && !error && teachers.length > 0 && (
            <>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border">
                <Button variant="outline" size="sm" onClick={selectAll}>
                  <CheckSquare className="h-4 w-4 mr-1" />
                  เลือกทั้งหมด
                </Button>
                <Button variant="outline" size="sm" onClick={deselectAll} disabled={selectedIds.size === 0}>
                  <Square className="h-4 w-4 mr-1" />
                  ยกเลิกทั้งหมด
                </Button>
                <span className="text-sm text-muted-foreground ml-auto">
                  เลือกแล้ว <span className="font-semibold text-foreground">{selectedIds.size}</span> คน
                </span>
                <Button size="sm" onClick={openConfirm} disabled={isSending}>
                  {isSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  ส่งครูที่เลือก
                </Button>
              </div>

              <div className="space-y-3">
                {teachers.map(teacher => (
                  <TeacherMatchCard
                    key={teacher.id}
                    teacher={teacher}
                    matchedAttributes={getMatchedAttributes(teacher)}
                    selected={selectedIds.has(teacher.id)}
                    onToggleSelect={() => toggleSelect(teacher.id)}
                    disabled={!teacher.has_line_link}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex flex-col items-center gap-2 pt-2">
                  <div className="text-sm text-muted-foreground">
                    ครูทั้งหมด <span className="font-semibold text-foreground">{totalCount.toLocaleString()}</span> คน • หน้า {page}/{totalPages}
                  </div>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => { e.preventDefault(); if (page > 1) setPage(page - 1); }}
                          className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                      {pageNumbers.map((p, i) => (
                        <PaginationItem key={`${p}-${i}`}>
                          {p === 'ellipsis' ? (
                            <PaginationEllipsis />
                          ) : (
                            <PaginationLink
                              href="#"
                              isActive={p === page}
                              onClick={(e) => { e.preventDefault(); setPage(p); }}
                            >
                              {p}
                            </PaginationLink>
                          )}
                        </PaginationItem>
                      ))}
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => { e.preventDefault(); if (page < totalPages) setPage(page + 1); }}
                          className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
            </TabsContent>
            <TabsContent value="history" className="mt-4">
              <MatchHistoryList />
            </TabsContent>
          </Tabs>
        </main>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>ยืนยันส่งข้อความหาครูที่เลือก</DialogTitle>
            <DialogDescription>
              เลือกนักเรียนและเวลาหมดอายุ จากนั้นกดยืนยันเพื่อส่งข้อความผ่าน LINE
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border p-3 bg-muted/30">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                ครูที่จะส่ง ({selectedTeachers.length} คน)
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {selectedTeachers.map(t => (
                  <span key={t.id} className="text-xs bg-background border rounded px-2 py-1">
                    {t.name}{t.nickname && ` (${t.nickname})`} <span className="text-muted-foreground font-mono">{t.code}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Read-only filter summary */}
            <div className="rounded-md border p-3 bg-muted/30 text-xs space-y-1">
              <div className="font-medium text-muted-foreground mb-1">ข้อมูลจากตัวกรอง (อ่านอย่างเดียว)</div>
              <div><span className="text-muted-foreground">วัน-เวลา:</span> {ctx.datetime}</div>
              <div><span className="text-muted-foreground">วิชา:</span> {ctx.subject} ({ctx.education_stage})</div>
              <div><span className="text-muted-foreground">สาขา:</span> {ctx.branch} | {ctx.class_program}</div>
            </div>

            {/* Teacher-attribute snapshot (disabled inputs — captured from filters) */}
            <div className="rounded-md border p-3 space-y-3">
              <div className="text-xs font-semibold text-muted-foreground">
                คุณสมบัติครูที่ต้องการ (จากตัวกรอง — แก้ไขไม่ได้)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">เฉพาะทาง</Label>
                  <Input disabled value={teacherAttrsDisplay.specialized} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">สไตล์การสอน</Label>
                  <Input disabled value={teacherAttrsDisplay.teachingStyles} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">ระดับสไตล์การสอน</Label>
                  <Input disabled value={teacherAttrsDisplay.styleLevels} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">บุคลิกภาพ (MBTI)</Label>
                  <Input disabled value={teacherAttrsDisplay.personality} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">ระดับนักเรียน</Label>
                  <Input disabled value={teacherAttrsDisplay.studentProficiency} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">เพศครู</Label>
                  <Input disabled value={teacherAttrsDisplay.gender} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs">คลาสทดลองเรียน</Label>
                  <Input disabled value={teacherAttrsDisplay.trialLesson} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
              </div>
            </div>

            {/* Student (locked from filter) */}
            <div className="space-y-1.5">
              <Label>นักเรียน (ล็อกตามตัวกรอง)</Label>
              {studentReady ? (
                <div className="rounded-md border p-3 bg-muted/30 text-sm">
                  {picked.label}
                </div>
              ) : (
                <div className="rounded-md border border-destructive/40 p-3 bg-destructive/5 text-xs text-destructive">
                  ยังไม่ได้เลือกนักเรียนในตัวกรอง — กรุณาปิดป๊อปอัพและเลือกนักเรียน (ในระบบ / ทดลองเรียน / พิมพ์เอง) ที่แถบตัวกรองก่อน
                </div>
              )}
            </div>

            {/* Expire selector */}
            <div className="space-y-1.5">
              <Label>ระยะเวลาหมดอายุของคำขอ</Label>
              <div className="flex gap-2">
                <Select value={expireHours} onValueChange={setExpireHours}>
                  <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 ชั่วโมง</SelectItem>
                    <SelectItem value="3">3 ชั่วโมง</SelectItem>
                    <SelectItem value="6">6 ชั่วโมง</SelectItem>
                    <SelectItem value="12">12 ชั่วโมง</SelectItem>
                    <SelectItem value="24">24 ชั่วโมง</SelectItem>
                    <SelectItem value="48">48 ชั่วโมง</SelectItem>
                    <SelectItem value="custom">กำหนดเอง</SelectItem>
                  </SelectContent>
                </Select>
                {expireHours === 'custom' && (
                  <Input
                    type="datetime-local"
                    value={customExpire}
                    onChange={e => setCustomExpire(e.target.value)}
                    max={new Date(Date.now() + 48 * 3600 * 1000).toISOString().slice(0, 16)}
                  />
                )}
              </div>
              {expireAtIso ? (
                <div className="text-xs text-muted-foreground">หมดอายุ: {new Date(expireAtIso).toLocaleString('th-TH')}</div>
              ) : (
                <div className="text-xs text-destructive">โปรดเลือกเวลาหมดอายุ (ไม่เกิน 48 ชั่วโมงนับจากตอนนี้)</div>
              )}
            </div>

            {/* 11-field form for LINE message details */}
            <div className="rounded-md border p-3 space-y-3">
              <div className="text-xs font-semibold text-muted-foreground">รายละเอียดข้อความ (กรอกก่อนส่ง)</div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">ชื่อเล่น <span className="text-destructive">*</span></Label>
                  <Input value={lineDetails.nickname} onChange={e => updateLD('nickname', e.target.value)} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">ชื่อจริง <span className="text-destructive">*</span></Label>
                  <Input value={lineDetails.firstName} onChange={e => updateLD('firstName', e.target.value)} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">นามสกุล</Label>
                  <Input value={lineDetails.lastName} onChange={e => updateLD('lastName', e.target.value)} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">โรงเรียน</Label>
                  <Input value={lineDetails.school} onChange={e => updateLD('school', e.target.value)} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">ระดับชั้น <span className="text-destructive">*</span></Label>
                  <Select value={lineDetails.grade || undefined} onValueChange={v => updateLD('grade', v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="เลือก" /></SelectTrigger>
                    <SelectContent>
                      {GRADE_OPTIONS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">วิชา</Label>
                  <Input
                    disabled
                    value={filters.subjects.length ? filters.subjects.join(', ') : '-'}
                    className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">หัวข้อที่ต้องการเรียนครั้งแรก</Label>
                  <Input value={lineDetails.trialTopic} onChange={e => updateLD('trialTopic', e.target.value)} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">ชั่วโมงเรียน</Label>
                  <Input type="number" step="0.5" min="0" value={lineDetails.hours} onChange={e => updateLD('hours', e.target.value)} className="h-8 text-sm disabled:bg-muted/40 disabled:opacity-100" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">ประเภทที่ลง <span className="text-destructive">*</span></Label>
                  <Select value={lineDetails.classType || undefined} onValueChange={v => updateLD('classType', v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="เลือก" /></SelectTrigger>
                    <SelectContent>
                      {CLASS_TYPE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">หลักสูตร <span className="text-destructive">*</span></Label>
                  <Select value={lineDetails.courseType || undefined} onValueChange={v => updateLD('courseType', v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="เลือก" /></SelectTrigger>
                    <SelectContent>
                      {COURSE_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {isInterType && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="space-y-1 sm:col-span-1">
                    <Label className="text-xs">การสื่อสาร <span className="text-destructive">*</span></Label>
                    <Select value={lineDetails.interSubType || undefined} onValueChange={v => updateLD('interSubType', v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="เลือก" /></SelectTrigger>
                      <SelectContent>
                        {COMMUNICATION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">วันที่ต้องการเรียน (จาก filter)</Label>
                  <div className="flex flex-wrap gap-1 min-h-8 items-center">
                    {slotDays.length === 0 ? (
                      <span className="text-xs text-destructive">— ยังไม่ได้เลือกวันใน filter</span>
                    ) : slotDays.map(d => (
                      <span key={d} className="text-xs bg-muted border rounded px-2 py-0.5">{d}</span>
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">เวลาที่ต้องการเรียน (จาก filter)</Label>
                  <div className="flex flex-wrap gap-1 min-h-8 items-center">
                    {slotTimes.length === 0 ? (
                      <span className="text-xs text-muted-foreground">— ไม่ระบุ</span>
                    ) : slotTimes.map(t => (
                      <span key={t} className="text-xs bg-muted border rounded px-2 py-0.5">{t}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">วันที่ต้องการเริ่มเรียน</Label>
                <Input type="date" value={lineDetails.desiredStartDate} onChange={e => updateLD('desiredStartDate', e.target.value)} className="h-8 text-sm w-48" />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">ความคาดหวังของผู้ปกครอง</Label>
                <Textarea value={lineDetails.parentExpectations} onChange={e => updateLD('parentExpectations', e.target.value)} rows={2} className="text-sm" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">ปัญหาที่ผู้ปกครองกังวล</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {PARENT_CONCERN_OPTIONS.map(opt => {
                    const checked = lineDetails.parentConcerns.includes(opt);
                    return (
                      <label key={opt} className="flex items-center gap-2 text-xs cursor-pointer">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(c) => {
                            updateLD('parentConcerns', c
                              ? [...lineDetails.parentConcerns, opt]
                              : lineDetails.parentConcerns.filter(x => x !== opt));
                          }}
                        />
                        <span>{opt}</span>
                      </label>
                    );
                  })}
                </div>
                <Input
                  placeholder="อื่น ๆ (ระบุ)"
                  value={lineDetails.parentConcernsOther}
                  onChange={e => updateLD('parentConcernsOther', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>

              {ldErrors.length > 0 && (
                <div className="text-xs text-destructive">ต้องกรอก/เลือก: {ldErrors.join(', ')}</div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">ตัวอย่างข้อความ (ใช้ชื่อครูคนแรก)</Label>
              <Textarea readOnly value={previewMessage} className="text-xs font-mono h-64" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={isSending}>
              ยกเลิก
            </Button>
            <Button onClick={handleSend} disabled={isSending}>
              {isSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              ยืนยันส่ง ({selectedTeachers.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
};

export default AdminTeacherMatchPage;
